/**
 * ResultPreview V2
 *
 * Zeigt das Ergebnis einer Image Operation:
 * - Preview des verarbeiteten Bildes
 * - Success Message mit Asset-ID oder Image-ID
 * - Dimensions und Processing Time
 * - "Zum Bild" Button → SingleImageMode
 * - "Neues Bild bearbeiten" Button → Reset
 *
 * @accessibility WCAG 2.1 AA + BFSG konform
 */

import { useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, RotateCcw, Tag, Maximize2, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button, Card, CardContent, Alert, AlertDescription } from '@/components/v2/ui';

export interface ResultPreviewProps {
  assetId: number | string;
  imageUrl: string;
  message?: string;
  originalDimensions?: { width: number; height: number };
  newDimensions?: { width: number; height: number };
  processingTimeMs?: number;
  onReset: () => void;
  className?: string;
}

export function ResultPreview({
  assetId,
  imageUrl,
  message = 'Erfolgreich verarbeitet!',
  originalDimensions,
  newDimensions,
  processingTimeMs,
  onReset,
  className,
}: ResultPreviewProps) {
  const navigate = useNavigate();

  const handleNavigateToImage = () => {
    navigate(`/v2/tools/generate/single?asset=${assetId}`);
  };

  return (
    <Card className={cn('border-green-200 dark:border-green-900', className)}>
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 bg-green-50 dark:bg-green-950/30 border-b border-green-200 dark:border-green-900 rounded-t-lg">
        <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
        <h3 className="text-lg font-semibold text-green-800 dark:text-green-200">
          {message}
        </h3>
      </div>

      <CardContent className="p-6 space-y-6">
        {/* Preview Image */}
        <div className="relative rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-800">
          <img
            src={imageUrl}
            alt="Verarbeitetes Bild"
            className="w-full max-h-[400px] object-contain"
          />
        </div>

        {/* Metadata Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Image ID */}
          <div className="flex flex-col items-center p-4 rounded-lg bg-neutral-50 dark:bg-neutral-800/50">
            <Tag className="h-5 w-5 text-primary-500 mb-2" />
            <span className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">
              Image ID
            </span>
            <span
              className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate max-w-full"
              title={String(assetId)}
            >
              {String(assetId).length > 12
                ? `${String(assetId).slice(0, 8)}...`
                : assetId}
            </span>
          </div>

          {/* Dimensions */}
          {originalDimensions && newDimensions && (
            <div className="flex flex-col items-center p-4 rounded-lg bg-neutral-50 dark:bg-neutral-800/50">
              <Maximize2 className="h-5 w-5 text-primary-500 mb-2" />
              <span className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">
                Dimensionen
              </span>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-neutral-500 dark:text-neutral-400">
                  {originalDimensions.width}x{originalDimensions.height}
                </span>
                <ArrowRight className="h-3 w-3 text-neutral-400" />
                <span className="font-semibold text-primary-600 dark:text-primary-400">
                  {newDimensions.width}x{newDimensions.height}
                </span>
              </div>
            </div>
          )}

          {/* Processing Time */}
          {processingTimeMs !== undefined && (
            <div className="flex flex-col items-center p-4 rounded-lg bg-neutral-50 dark:bg-neutral-800/50">
              <Clock className="h-5 w-5 text-green-500 mb-2" />
              <span className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">
                Verarbeitungszeit
              </span>
              <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                {processingTimeMs}ms
              </span>
            </div>
          )}
        </div>

        {/* Success Alert */}
        <Alert variant="success">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>
            Bild wurde erfolgreich in deinem Workspace gespeichert!
          </AlertDescription>
        </Alert>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="primary"
            className="flex-1"
            onClick={handleNavigateToImage}
          >
            <ArrowRight className="h-4 w-4 mr-2" />
            Zum Bild
          </Button>

          <Button
            variant="outline"
            className="flex-1"
            onClick={onReset}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Neues Bild bearbeiten
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default ResultPreview;
